// Subscription models mirroring the Care API subscription DTOs.
// Enums are numeric to match the backend (Care.Shared.Enums).

export enum SubscriptionStatus {
  Pending = 0,
  Trialing = 1,
  Active = 2,
  PastDue = 3,
  Cancelled = 4,
  Expired = 5,
}

export enum PaymentMode {
  Manual = 0,
  Gateway = 1,
}

export enum PaymentMethodType {
  Other = 0,
  Bank = 1,
  Bkash = 2,
  Nagad = 3,
  Cash = 4,
}

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  [SubscriptionStatus.Pending]: "Pending",
  [SubscriptionStatus.Trialing]: "Trialing",
  [SubscriptionStatus.Active]: "Active",
  [SubscriptionStatus.PastDue]: "Past Due",
  [SubscriptionStatus.Cancelled]: "Cancelled",
  [SubscriptionStatus.Expired]: "Expired",
};

// Badge tone: Active / Trialing → success, Pending → warning,
// PastDue / Cancelled / Expired → danger.
export const SUBSCRIPTION_STATUS_CLASS: Record<SubscriptionStatus, string> = {
  [SubscriptionStatus.Pending]: "warning",
  [SubscriptionStatus.Trialing]: "success",
  [SubscriptionStatus.Active]: "success",
  [SubscriptionStatus.PastDue]: "danger",
  [SubscriptionStatus.Cancelled]: "danger",
  [SubscriptionStatus.Expired]: "danger",
};

export const PAYMENT_MODE_LABELS: Record<PaymentMode, string> = {
  [PaymentMode.Manual]: "Manual",
  [PaymentMode.Gateway]: "Gateway",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethodType, string> = {
  [PaymentMethodType.Other]: "Other",
  [PaymentMethodType.Bank]: "Bank",
  [PaymentMethodType.Bkash]: "bKash",
  [PaymentMethodType.Nagad]: "Nagad",
  [PaymentMethodType.Cash]: "Cash",
};

export const PAYMENT_METHOD_OPTIONS: {
  value: PaymentMethodType;
  label: string;
}[] = Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({
  value: Number(value) as PaymentMethodType,
  label,
}));

export interface PlanFeature {
  id: string | null;
  planId: string | null;
  featureKey: string;
  featureValue: string;
  createdAt?: string;
}

export interface Plan {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  priceMonthly: number;
  currency: string;
  billingPeriodDays: number;
  isActive: boolean;
  isPublic: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
  features?: PlanFeature[];
}

export interface OrgEntitlements {
  hasActiveSubscription: boolean;
  subscriptionId: string | null;
  planId: string | null;
  planCode: string | null;
  planName: string | null;
  status: number;
  startsAt: string | null;
  endsAt: string | null;
  messagingEnabled: boolean;
  monthlyMessageLimit: number;
  messagesUsedThisMonth: number;
  messagesRemaining: number;
  websiteEnabled: boolean;
  maxBranches: number;
}

export interface OrganizationSubscription {
  id: string | null;
  organizationId: string;
  planId: string;
  status: number;
  paymentMode: number;
  startsAt: string;
  endsAt: string;
  externalReference?: string | null;
  notes?: string | null;
  activatedBy?: string | null;
  isCurrent?: boolean;
  createdAt: string;
  updatedAt: string;
  planCode?: string | null;
  planName?: string | null;
}

export interface ActivateSubscriptionRequest {
  organizationId: string;
  planId: string;
  periodCount: number;
  amount: number;
  paymentMethod: number;
  paymentReference?: string | null;
  notes?: string | null;
  paidAt?: string | null;
}

export interface ExtendSubscriptionRequest {
  organizationId: string;
  periodCount: number;
  amount: number;
  paymentMethod: number;
  paymentReference?: string | null;
  notes?: string | null;
}

export interface CancelSubscriptionRequest {
  organizationId: string;
  notes?: string | null;
}